import { Radio, Smartphone, Siren, ShieldCheck, Home, TriangleAlert } from "lucide-react";

// Static, authoritative safety board — airs when the threat is elevated or a watch/warning is active.
// Styled for stream legibility: color-coded cards, Oswald headings + readable IBM Plex body copy.
const WAYS = [
  { icon: Smartphone, title: "Phone Alerts", tint: "#2f7bed", body: "Keep Wireless Emergency Alerts on. They sound even on silent for a Tornado Warning." },
  { icon: Radio, title: "NOAA Weather Radio", tint: "#1fae6a", body: "Program it for your county. It wakes you for warnings and a power outage won't stop it." },
  { icon: Siren, title: "Outdoor Sirens", tint: "#f6a21c", body: "They're meant for people outside. Never count on hearing them indoors or while asleep." },
  { icon: ShieldCheck, title: "Have a Backup", tint: "#a568e6", body: "Use at least two methods — one can fail. Know every county you live, work and travel in." },
];

const PLAN = [
  "Pick your safe spot now: lowest floor, center of the building, no windows.",
  "Charge your phone and devices in case the power goes out.",
  "Keep shoes, a helmet and a flashlight within reach of that safe spot.",
];

export function SafetySlide({ threat }) {
  const watch = threat?.active_watch;
  const warns = threat?.active_warnings || 0;
  const subhead = warns > 0
    ? "Warnings are in effect right now — act the moment yours is issued."
    : watch
      ? `A ${watch === "tornado" ? "Tornado" : "Severe Thunderstorm"} Watch is in effect — be ready to act fast.`
      : "Severe weather is possible — make sure a warning will actually reach you.";

  return (
    <div data-testid="safety-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(140deg, #101b30 0%, #0b1120 55%, #1c0f06 100%)" }}>
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 28px)" }} />
      <div className="relative flex h-full w-full flex-col px-[54px] py-[30px]">
        {/* Header */}
        <div className="flex items-center gap-2.5">
          <span className="flex items-center gap-2 rounded-full bg-[#ffd60a] px-3.5 py-1 font-oswald text-[16px] font-black uppercase tracking-[0.2em] text-[#0a0f1c]">
            <TriangleAlert className="h-5 w-5" strokeWidth={2.6} /> Weather Safety
          </span>
        </div>
        <div className="mt-2.5 font-oswald text-[62px] font-black uppercase leading-[0.9] text-white">Have a Way to Get Warnings</div>
        <div className="mt-2 font-plex text-[25px] font-semibold leading-snug text-[#ffd60a]">{subhead}</div>

        {/* Ways to get warnings */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          {WAYS.map((w, i) => (
            <div key={i} className="flex flex-col gap-3 overflow-hidden rounded-[12px] bg-white/[0.06] ring-1 ring-white/10" data-testid={`safety-way-${i}`} style={{ borderTop: `5px solid ${w.tint}` }}>
              <div className="flex flex-col gap-3 p-5">
                <span className="flex h-14 w-14 items-center justify-center rounded-[12px]" style={{ background: `${w.tint}26` }}>
                  <w.icon className="h-8 w-8" strokeWidth={2.2} style={{ color: w.tint }} />
                </span>
                <div className="font-oswald text-[26px] font-black uppercase leading-none text-white">{w.title}</div>
                <div className="font-plex text-[22px] font-medium leading-snug text-white/85">{w.body}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Make a plan */}
        <div className="mt-6 flex flex-1 flex-col justify-center gap-3">
          <div className="font-oswald text-[28px] font-black uppercase tracking-wide text-white">Make a Plan Before It Hits</div>
          <div className="grid grid-cols-3 gap-4">
            {PLAN.map((t, i) => (
              <div key={i} className="flex items-center gap-4 rounded-[10px] bg-white/[0.05] px-4 py-4 ring-1 ring-white/10" data-testid={`safety-plan-${i}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ffd60a] font-oswald text-[26px] font-black text-[#0a0f1c]">{i + 1}</span>
                <span className="font-plex text-[23px] font-semibold leading-snug text-white">{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Shelter action */}
        <div className="mt-6 flex items-center gap-4 rounded-[12px] bg-[#d90429] px-6 py-4 shadow-lg" data-testid="safety-action">
          <Home className="h-10 w-10 shrink-0 text-white" strokeWidth={2.2} />
          <div className="font-plex text-[27px] font-bold leading-tight text-white">
            If a Tornado Warning is issued: go to a small interior room on the lowest floor, away from windows — and cover your head.
          </div>
        </div>
        <div className="mt-2.5 font-plex text-[19px] font-medium tracking-wide text-white/55">
          Warnings are issued by the National Weather Service. This stream is not an official warning source.
        </div>
      </div>
    </div>
  );
}
